import { FormFeedBack, Select, convertEventSelectToTarget } from '@repo/ui';
import { locale } from '@repo/ui';
import { RECURRENCE_TYPE } from 'domain/value-objects/recurrence-type';
import { FormikValues } from 'formik';
import Flatpickr from 'react-flatpickr';
import { Row, Col, Label, Input } from 'reactstrap';
import { useWeeklyDays } from '../../hooks/useWeeklyDays';

interface CustomSchedulingPushNotificationProps {
  validation: FormikValues;
  customRecurrenceUnitOptions?: Array<{
    value: number | string;
    label: string;
  }>;
}

const DAYS_OF_WEEK =
  locale.weekdays?.shorthand?.map((short, index) => ({
    value: index,
    label: locale.weekdays?.longhand?.[index] || short,
    short,
  })) || [];

export const CustomSchedulingPushNotification = ({
  validation,
  customRecurrenceUnitOptions,
}: CustomSchedulingPushNotificationProps) => {
  const { handleDayOfWeekChange, isDaySelected } = useWeeklyDays(validation);

  const showWeeklyDaysSelection =
    validation.values.recurrencePushNotification?.value ===
      RECURRENCE_TYPE.WEEKLY ||
    (validation.values.recurrencePushNotification?.value ===
      RECURRENCE_TYPE.CUSTOM &&
      validation.values.customRecurrenceUnit?.value === RECURRENCE_TYPE.WEEKLY);

  return (
    <Row className="mt-3">
      {showWeeklyDaysSelection && (
        <Col md={12} className="mb-3">
          <Label>Dias da Semana</Label>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {DAYS_OF_WEEK.map((day) => (
              <div key={day.value} className="form-check form-check-inline">
                <Input
                  className="form-check-input"
                  type="checkbox"
                  id={`weekDay${day.value}`}
                  checked={isDaySelected(day.value)}
                  onChange={() => handleDayOfWeekChange(day.value)}
                  onBlur={validation.handleBlur}
                />
                <Label
                  className="form-check-label user-select-none"
                  htmlFor={`weekDay${day.value}`}
                >
                  {day.short}
                </Label>
              </div>
            ))}
          </div>
          <FormFeedBack
            error={validation.errors?.weeklyDays as string}
            isInvalid={
              !!(validation.touched.weeklyDays && validation.errors.weeklyDays)
            }
          />
        </Col>
      )}

      {validation.values.recurrencePushNotification?.value !==
        RECURRENCE_TYPE.DONT_REPEAT &&
        validation.values.recurrencePushNotification?.value !== undefined &&
        validation.values.recurrencePushNotification?.value !== null && (
          <>
            <Col md={3}>
              <Label>Termina</Label>
              <div className="d-flex flex-column gap-3">
                <div className="form-check">
                  <Input
                    className="form-check-input"
                    type="radio"
                    id="dateEndAtPushNotification"
                    name="endAtPushNotification"
                    onBlur={validation.handleBlur}
                    value="date"
                    onChange={() => {
                      validation.setFieldValue('endAtPushNotification', 'date');
                      validation.setFieldValue('endAtOccurrences', 1);
                    }}
                    checked={validation.values.endAtPushNotification === 'date'}
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="dateEndAtPushNotification"
                  >
                    Em
                    <Flatpickr
                      className="form-control"
                      placeholder="Selecione a data e hora"
                      name="endAtDate"
                      value={validation.values.endAtDate}
                      onChange={(selectedDates) => {
                        let selectedDate = selectedDates[0] || null;

                        if (selectedDate) {
                          const minutes = selectedDate.getMinutes();
                          const roundedMinutes = Math.round(minutes / 30) * 30;
                          selectedDate.setMinutes(roundedMinutes % 60);
                        }

                        validation.setFieldValue('endAtDate', selectedDate);
                      }}
                      onBlur={validation.handleBlur}
                      disabled={
                        validation.values.endAtPushNotification !== 'date'
                      }
                      options={{
                        enableTime: true,
                        dateFormat: 'd/m/Y H:i',
                        time_24hr: true,
                        minDate: 'today',
                        minuteIncrement: 30,
                        locale: locale,
                        defaultHour: 8,
                        defaultMinute: 0,
                      }}
                    />
                  </Label>
                </div>

                <div className="form-check">
                  <Input
                    className="form-check-input"
                    type="radio"
                    id="occurrencesEndAtPushNotification"
                    name="endAtPushNotification"
                    onBlur={validation.handleBlur}
                    value="occurrences"
                    onChange={() => {
                      validation.setFieldValue(
                        'endAtPushNotification',
                        'occurrences',
                      );
                      validation.setFieldValue('endAtDate', null);
                    }}
                    checked={
                      validation.values.endAtPushNotification === 'occurrences'
                    }
                  />
                  <Label
                    className="form-check-label"
                    htmlFor="occurrencesEndAtPushNotification"
                  >
                    Após
                    <Input
                      type="number"
                      min="1"
                      className="form-control"
                      style={{ width: '100px' }}
                      placeholder="1"
                      name="endAtOccurrences"
                      value={validation.values.endAtOccurrences}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      disabled={
                        validation.values.endAtPushNotification !==
                        'occurrences'
                      }
                    />
                    ocorrência(s)
                  </Label>
                </div>
              </div>
              <FormFeedBack
                error={validation.errors?.endAtPushNotification as string}
                isInvalid={
                  !!(
                    validation.touched.endAtPushNotification &&
                    validation.errors.endAtPushNotification
                  )
                }
              />
              <FormFeedBack
                error={validation.errors?.endAtDate as string}
                isInvalid={
                  !!(
                    validation.touched.endAtDate && validation.errors.endAtDate
                  )
                }
              />
              <FormFeedBack
                error={validation.errors?.endAtOccurrences as string}
                isInvalid={
                  !!(
                    validation.touched.endAtOccurrences &&
                    validation.errors.endAtOccurrences
                  )
                }
              />
            </Col>
            {validation.values.recurrencePushNotification?.value ===
              RECURRENCE_TYPE.CUSTOM && (
              <Col md={3}>
                <Label htmlFor="customRecurrencePushNotification">
                  Repetir a cada
                </Label>
                <div className="d-flex align-items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="365"
                    className="form-control"
                    style={{ width: '80px' }}
                    placeholder="1"
                    name="customRecurrenceInterval"
                    value={validation.values.customRecurrenceInterval}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      !!(
                        validation.touched.customRecurrenceInterval &&
                        validation.errors.customRecurrenceInterval
                      )
                    }
                  />
                  <Select
                    placeholder="Selecione"
                    name="customRecurrenceUnit"
                    value={validation.values.customRecurrenceUnit}
                    onChange={(e) => {
                      validation.handleChange(
                        convertEventSelectToTarget('customRecurrenceUnit', e),
                      );
                    }}
                    onBlur={validation.handleBlur}
                    options={customRecurrenceUnitOptions}
                    className="flex-grow-1"
                    invalid={
                      !!(
                        validation.touched.customRecurrenceUnit &&
                        validation.errors.customRecurrenceUnit
                      )
                    }
                  />
                </div>
                <FormFeedBack
                  error={validation.errors?.customRecurrenceInterval as string}
                  isInvalid={
                    !!(
                      validation.touched.customRecurrenceInterval &&
                      validation.errors.customRecurrenceInterval
                    )
                  }
                />
                <FormFeedBack
                  error={validation.errors?.customRecurrenceUnit as string}
                  isInvalid={
                    !!(
                      validation.touched.customRecurrenceUnit &&
                      validation.errors.customRecurrenceUnit
                    )
                  }
                />
              </Col>
            )}
          </>
        )}
    </Row>
  );
};
